/**
 * Auth Token Management
 *
 * Manages Better Auth JWT token providers for authenticatedFetch().
 * Extracted from db-client.ts during Phase 3.4 DPC elimination.
 *
 * @module auth-token
 */

// ---------------------------------------------------------------------------
// Auth status state machine
// ---------------------------------------------------------------------------

/**
 * Auth status values:
 *  - 'anonymous'     — Phase 2+ (NocoDB #3843 / EPIC #3840): anon BA session in flight or
 *                      active. When FF_BA_ANONYMOUS_BOOTSTRAP is ON, the client calls
 *                      authClient.signIn.anonymous() on first mount so this state carries
 *                      a real BA JWT (not just an X-Device-Id). The on-401 retry and
 *                      auth_broken recovery paths remain functionally unchanged.
 *                      Pre-Phase-2 / flag OFF: no BA session at all; send X-Device-Id.
 *  - 'pending'       — BA session present, JWT mint in flight, gate fetches via awaitAuthReady
 *  - 'authenticated' — BA session + valid JWT, send Bearer
 *  - 'auth_broken'   — BA session present but JWT mint failed after retries.
 *                      authenticatedFetch must REFUSE to send the request and trigger
 *                      onAuthError so the UI can sign the user out and force re-auth.
 *                      Without this state, AuthContext used to fall back to 'anonymous'
 *                      and silently downgrade logged-in users to anon-tier data —
 *                      the root cause of NocoDB #3829 / Sentry DRAMA-WEB-3B.
 */
export type AuthStatus = 'anonymous' | 'pending' | 'authenticated' | 'auth_broken';
let _authStatus: AuthStatus = 'anonymous';
let _pendingReady: { promise: Promise<void>; resolve: () => void } | null = null;

// Bootstrap signal: resolves on first setAuthStatus call. Default 'anonymous'
// status would otherwise let cold-load fetches skip the pre-gate and ship
// without a Bearer header. NocoDB #4100 / Sentry DRAMA-EDGE-17.
//
// `!:` definite-assignment: the Promise executor below runs synchronously and
// assigns _bootstrapResolve before construction returns. A `() => {}` default
// would be unreachable by construction, which trips the 100% function-coverage
// gate on this critical-auth file.
let _bootstrapResolve!: () => void;
let _bootstrapPromise: Promise<void> = new Promise<void>((resolve) => {
  _bootstrapResolve = resolve;
});
let _bootstrapped = false;

export function setAuthStatus(status: AuthStatus): void {
  if (!_bootstrapped) {
    _bootstrapped = true;
    _bootstrapResolve();
  }

  _authStatus = status;
  // Resolve pending promise when leaving 'pending' state
  if (status !== 'pending' && _pendingReady) {
    _pendingReady.resolve();
    _pendingReady = null;
  }
  // Create pending promise when entering 'pending' state
  if (status === 'pending' && !_pendingReady) {
    let resolve: () => void;
    const promise = new Promise<void>(r => { resolve = r; });
    _pendingReady = { promise, resolve: resolve! };
  }
}

export function getAuthStatus(): AuthStatus {
  return _authStatus;
}

export function awaitAuthReady(): Promise<void> {
  if (!_pendingReady) return Promise.resolve();
  return _pendingReady.promise;
}

/**
 * Wait for AuthContext to bootstrap (i.e., to make its first call to
 * setAuthStatus). Resolves immediately if bootstrap already happened.
 *
 * Use this when you're about to send a request that needs auth and the
 * current status is still 'anonymous' — wait briefly to let AuthContext
 * either confirm anonymous or transition to 'pending'/'authenticated'.
 */
export function awaitAuthBootstrap(): Promise<void> {
  return _bootstrapPromise;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/** Callback to retrieve the Better Auth access token. */
let _getAccessToken: (() => string | null) | null = null;

/** Callback to refresh an expired Better Auth JWT. */
let _refreshAccessToken: (() => Promise<string | null>) | null = null;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Configure the auth token providers.
 *
 * Call this once during app initialisation (alongside `initClientConfig()`).
 *
 * @param getAccessToken - Returns the current BA JWT (or null when not yet available).
 * @param refreshAccessToken - Optional callback to refresh an expired JWT.
 */
export function configureAuthToken(
  getAccessToken?: () => string | null,
  refreshAccessToken?: () => Promise<string | null>,
): void {
  _getAccessToken = getAccessToken ?? null;
  _refreshAccessToken = refreshAccessToken ?? null;
}

/**
 * @internal Expose the token provider for authenticatedFetch.
 */
export function getAccessTokenFn(): (() => string | null) | null {
  return _getAccessToken;
}

/**
 * @internal Expose the refresh callback for authenticatedFetch.
 */
export function getRefreshAccessTokenFn(): (() => Promise<string | null>) | null {
  return _refreshAccessToken;
}

/**
 * Reset module state. For tests only.
 *
 * Recreates the bootstrap promise. Any code already awaiting the OLD
 * promise stays unblocked (the previous promise was resolved on first
 * setAuthStatus before reset); new awaiters get the fresh pending one.
 *
 * @internal
 */
export function resetAuthToken(): void {
  _getAccessToken = null;
  _refreshAccessToken = null;
  _authStatus = 'anonymous';
  if (_pendingReady) { _pendingReady.resolve(); _pendingReady = null; }
  _bootstrapped = false;
  _bootstrapPromise = new Promise<void>((resolve) => {
    _bootstrapResolve = resolve;
  });
}
